import { useEffect, useState } from 'react';

import { fetchOnboardingStatus } from './api';

/**
 * Tiny shared store for /api/onboarding/status (one call per page view for
 * people still onboarding; once per load for finished accounts).
 */

let state = { userId: null, status: null, loading: false, at: 0 };
const listeners = new Set();
let inFlight = null;

const emit = () => listeners.forEach(fn => fn(state));

const setState = next => {
  state = { ...state, ...next };
  emit();
};

/**
 * Load (or refresh) the status for this user.
 *
 * @param {string} userId
 * @param {{ fresh?: boolean, force?: boolean }} [options]
 * @returns {Promise<Object|null>}
 */
export const loadOnboardingStatusFor = (userId, { fresh = false, force = false } = {}) => {
  if (!userId || typeof window === 'undefined') {
    return Promise.resolve(null);
  }
  if (state.userId !== userId) {
    state = { userId, status: null, loading: false, at: 0 };
  }
  // Finished accounts: one call per page load is enough.
  if (!force && state.status?.allDone) {
    return Promise.resolve(state.status);
  }
  if (inFlight && !fresh) {
    return inFlight;
  }
  setState({ loading: true });
  inFlight = fetchOnboardingStatus({ fresh })
    .then(status => {
      if (state.userId === userId) {
        setState({ status: status || null, loading: false, at: Date.now() });
      }
      return status;
    })
    .catch(() => {
      // Fail open on the client: the server still guards every action.
      setState({ loading: false });
      return null;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
};

export const getOnboardingStatusSnapshot = () => state;

export const resetOnboardingStatusForTests = () => {
  state = { userId: null, status: null, loading: false, at: 0 };
  inFlight = null;
  listeners.clear();
};

/**
 * Subscribe to the status. Refreshes on every `refreshKey` change while the
 * account is still onboarding.
 *
 * @param {string|null} userId
 * @param {string} [refreshKey] e.g. location.key / pathname
 * @param {{ enabled?: boolean }} [options]
 * @returns {{ status: Object|null, loading: boolean, refresh: Function }}
 */
export const useOnboardingStatus = (userId, refreshKey = '', { enabled = true } = {}) => {
  const [snap, setSnap] = useState(state);
  useEffect(() => {
    listeners.add(setSnap);
    return () => listeners.delete(setSnap);
  }, []);
  useEffect(() => {
    if (enabled && userId) {
      loadOnboardingStatusFor(userId);
    }
  }, [userId, refreshKey, enabled]);
  const mine = snap.userId === userId ? snap : { status: null, loading: false };
  return {
    status: mine.status,
    loading: !!mine.loading,
    refresh: (opts = {}) => loadOnboardingStatusFor(userId, { fresh: true, force: true, ...opts }),
  };
};
